import { useEffect, useState } from "react";
import {
  fetchWfDailyAttendance,
  startWfRecompute,
  ingestBiometricPunch,
  createWfFreeze,
  fetchWfFreezes,
  releaseWfFreeze,
  previewCalendarDay,
  submitAttendanceCorrectionRequest,
} from "../../../Moduels/attendance/wfApi";
import { fetchMyOrganisationSummary } from "../../../Moduels/attendance/attendanceApi";
import AttendanceOverrideModal from "./AttendanceOverrideModal";
import EmployeeSelect from "../../common/EmployeeSelect";
import { resolveEmployeeDisplay } from "../../common/displayUtils";
import { useEmployees } from "../../../hooks/useEmployees";
import DatePickerField from "../../common/DatePickerField";
import DateTimePickerField from "../../common/DateTimePickerField";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function errMsg(e) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d && typeof d === "object") return d.message || d.code || JSON.stringify(d);
  return e?.message || "Something went wrong";
}

function today() {
  return new Date().toISOString().slice(0, 10);
}

function monthStart() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}

const STATUS_BADGE = {
  P: "bg-green-100 text-green-800",
  A: "bg-red-100 text-red-700",
  HD: "bg-yellow-100 text-yellow-800",
  L: "bg-blue-100 text-blue-700",
  H: "bg-purple-100 text-purple-700",
  WO: "bg-gray-100 text-gray-600",
  present: "bg-green-100 text-green-800",
  absent: "bg-red-100 text-red-700",
  half_day: "bg-yellow-100 text-yellow-800",
  leave: "bg-blue-100 text-blue-700",
  holiday: "bg-purple-100 text-purple-700",
  no_data: "bg-gray-100 text-gray-400",
};

function StatusBadge({ status }) {
  const cls = STATUS_BADGE[status] || "bg-gray-100 text-gray-500";
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      {status || "—"}
    </span>
  );
}

const TABS = [
  { id: "daily", label: "Daily Attendance" },
  { id: "biometric", label: "Biometric Ingest" },
  { id: "freeze", label: "Freeze Manager" },
  { id: "calendar", label: "Calendar Preview" },
  { id: "correction", label: "Correction Request" },
];

// ---------------------------------------------------------------------------
// Sub-tabs
// ---------------------------------------------------------------------------

function DailyAttendanceTab({ orgId }) {
  const { employees } = useEmployees();
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [overrideTarget, setOverrideTarget] = useState(null);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const data = await fetchWfDailyAttendance({ from_date: from, to_date: to });
      setRows(data);
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  const handleRecompute = async (employeeId) => {
    setMsg("");
    setErr("");
    try {
      await startWfRecompute({ employee_id: employeeId, from_date: from, to_date: to });
      setMsg("Recompute triggered.");
      setTimeout(load, 1500);
    } catch (e) {
      setErr(errMsg(e));
    }
  };

  useEffect(() => {
    load();
  }, [from, to]);

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-4 items-end">
        <DatePickerField label="From" compact value={from} onChange={setFrom} max={to || undefined} />
        <DatePickerField label="To" compact value={to} onChange={setTo} min={from || undefined} />
        <button type="button" onClick={load} className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm">
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {err && <div className="text-red-600 text-sm mb-3">{err}</div>}
      {msg && <div className="text-green-700 text-sm mb-3">{msg}</div>}

      <div className="overflow-x-auto rounded-xl border">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
            <tr>
              <th className="px-4 py-3 text-left">Date</th>
              <th className="px-4 py-3 text-left">Employee</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-right">Hours</th>
              <th className="px-4 py-3 text-right">Fraction</th>
              <th className="px-4 py-3 text-center">Locked</th>
              <th className="px-4 py-3 text-center">Protected</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-slate-400 text-sm">
                  {loading ? "Loading…" : "No records found."}
                </td>
              </tr>
            )}
            {rows.map((row) => (
              <tr key={row.attendance_id} className="hover:bg-slate-50 transition-colors">
                <td className="px-4 py-3 font-mono text-xs text-slate-600">{row.work_date}</td>
                <td className="px-4 py-3 text-sm text-slate-800">
                  {resolveEmployeeDisplay(row, employees)}
                </td>
                <td className="px-4 py-3"><StatusBadge status={row.status} /></td>
                <td className="px-4 py-3 text-right text-slate-700">{row.work_hours ?? "—"}</td>
                <td className="px-4 py-3 text-right text-slate-600">{row.day_fraction ?? "—"}</td>
                <td className="px-4 py-3 text-center">
                  {row.is_locked ? (
                    <span className="text-red-500 text-xs font-semibold">Yes</span>
                  ) : (
                    <span className="text-slate-300 text-xs">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-center">
                  {row.policy_result_json?.recompute_protected ? (
                    <span className="text-amber-500 text-xs font-semibold">Yes</span>
                  ) : (
                    <span className="text-slate-300 text-xs">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setOverrideTarget(row)}
                      className="text-xs px-2 py-1 rounded border border-slate-300 hover:bg-slate-100 text-slate-700"
                    >
                      Override
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRecompute(row.employee_id)}
                      className="text-xs px-2 py-1 rounded border border-teal-300 hover:bg-teal-50 text-teal-700"
                    >
                      Recompute
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {overrideTarget && (
        <AttendanceOverrideModal
          attendance={overrideTarget}
          onClose={() => setOverrideTarget(null)}
          onSaved={(m) => {
            setMsg(m);
            setOverrideTarget(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function BiometricIngestTab() {
  const { employees } = useEmployees();
  const [form, setForm] = useState({
    terminal_code: "",
    device_employee_code: "",
    punch_time: "",
    event_type: "IN",
  });
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const handlePunch = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErr("");
    setResult(null);
    try {
      const r = await ingestBiometricPunch({
        terminal_code: form.terminal_code,
        device_employee_code: form.device_employee_code,
        punch_time: form.punch_time || new Date().toISOString(),
        event_type: form.event_type,
      });
      setResult(r);
    } catch (ex) {
      setErr(errMsg(ex));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-lg">
      <p className="text-sm text-slate-500 mb-4">
        Submit a single realtime biometric punch for testing device integration. Batch ingest is available via the API.
      </p>
      {err && <div className="text-red-600 text-sm mb-3">{err}</div>}
      {result && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
          <div className="font-medium mb-1">Punch {result.status}</div>
          <div className="text-xs space-y-0.5">
            <div>Employee: {resolveEmployeeDisplay(result, employees)}</div>
            {result.duplicate && <div className="text-amber-700">Duplicate punch</div>}
            {result.anomaly && <div className="text-red-700">Anomaly detected</div>}
          </div>
        </div>
      )}

      <form onSubmit={handlePunch} className="bg-white border rounded-xl p-4 space-y-3">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Terminal code</label>
          <input
            required
            type="text"
            placeholder="e.g. GATE-A-01"
            className="w-full border rounded-lg px-3 py-2 text-sm"
            value={form.terminal_code}
            onChange={(e) => setForm({ ...form, terminal_code: e.target.value })}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Device employee code</label>
          <input
            required
            type="text"
            placeholder="Biometric template / card number"
            className="w-full border rounded-lg px-3 py-2 text-sm"
            value={form.device_employee_code}
            onChange={(e) => setForm({ ...form, device_employee_code: e.target.value })}
          />
        </div>
        <div className="flex gap-3">
          <div className="flex-1">
            <DateTimePickerField
              label="Punch time"
              value={form.punch_time}
              onChange={(val) => setForm({ ...form, punch_time: val })}
              placeholder="Select punch date & time"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Event</label>
            <select
              className="border rounded-lg px-3 py-2 text-sm"
              value={form.event_type}
              onChange={(e) => setForm({ ...form, event_type: e.target.value })}
            >
              <option value="IN">IN</option>
              <option value="OUT">OUT</option>
              <option value="BREAK_START">BREAK_START</option>
              <option value="BREAK_END">BREAK_END</option>
            </select>
          </div>
        </div>
        <button
          type="submit"
          disabled={saving}
          className="w-full bg-slate-800 text-white py-2 rounded-lg text-sm font-medium disabled:opacity-50"
        >
          {saving ? "Ingesting…" : "Ingest punch"}
        </button>
      </form>
    </div>
  );
}

function FreezeManagerTab() {
  const [form, setForm] = useState({ freeze_level: "attendance", range_start: "", range_end: "", notes: "" });
  const [freezes, setFreezes] = useState([]);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const loadFreezes = async () => {
    try {
      const data = await fetchWfFreezes();
      setFreezes(Array.isArray(data) ? data : data?.items ?? []);
    } catch {
      // non-critical
    }
  };

  useEffect(() => {
    loadFreezes();
  }, []);

  const handleApply = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg("");
    setErr("");
    try {
      const payload = { freeze_level: form.freeze_level, notes: form.notes || undefined };
      if (form.range_start) payload.range_start = form.range_start;
      if (form.range_end) payload.range_end = form.range_end;
      await createWfFreeze(payload);
      setMsg(`${form.freeze_level} freeze applied.`);
      setForm({ freeze_level: "attendance", range_start: "", range_end: "", notes: "" });
      loadFreezes();
    } catch (ex) {
      setErr(errMsg(ex));
    } finally {
      setSaving(false);
    }
  };

  const handleRelease = async (freezeId) => {
    if (!window.confirm("Release this freeze?")) return;
    try {
      await releaseWfFreeze(freezeId);
      setMsg("Freeze released.");
      loadFreezes();
    } catch (ex) {
      setErr(errMsg(ex));
    }
  };

  const FREEZE_LEVELS = [
    { value: "attendance", label: "Attendance freeze" },
    { value: "payroll", label: "Payroll freeze" },
    { value: "financial", label: "Financial freeze (P9)" },
  ];

  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div>
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Apply freeze</h3>
        {msg && <div className="text-green-700 text-sm mb-3">{msg}</div>}
        {err && <div className="text-red-600 text-sm mb-3">{err}</div>}
        <form onSubmit={handleApply} className="bg-white border rounded-xl p-4 space-y-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Freeze level</label>
            <select
              className="w-full border rounded-lg px-3 py-2 text-sm"
              value={form.freeze_level}
              onChange={(e) => setForm({ ...form, freeze_level: e.target.value })}
            >
              {FREEZE_LEVELS.map((l) => (
                <option key={l.value} value={l.value}>{l.label}</option>
              ))}
            </select>
          </div>
          <div className="flex gap-2">
            <DatePickerField
              label="From"
              className="flex-1"
              value={form.range_start}
              onChange={(date) => setForm({ ...form, range_start: date })}
              max={form.range_end || undefined}
            />
            <DatePickerField
              label="To"
              className="flex-1"
              value={form.range_end}
              onChange={(date) => setForm({ ...form, range_end: date })}
              min={form.range_start || undefined}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Notes</label>
            <input type="text" placeholder="Optional notes" className="w-full border rounded-lg px-3 py-2 text-sm" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <button type="submit" disabled={saving} className="w-full bg-slate-800 text-white py-2 rounded-lg text-sm font-medium disabled:opacity-50">
            {saving ? "Applying…" : "Apply freeze"}
          </button>
        </form>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-slate-700 mb-3">Active freezes</h3>
        <div className="space-y-2">
          {freezes.length === 0 && <p className="text-sm text-slate-400">No active freezes.</p>}
          {freezes.map((f) => (
            <div key={f.freeze_id} className="bg-white border rounded-xl p-3 flex justify-between items-start">
              <div className="text-sm">
                <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium mr-2 ${
                  f.freeze_level === "financial" ? "bg-red-100 text-red-700" :
                  f.freeze_level === "payroll" ? "bg-orange-100 text-orange-700" :
                  "bg-blue-100 text-blue-700"
                }`}>
                  {f.freeze_level}
                </span>
                <span className="text-slate-600 text-xs">
                  {f.range_start && f.range_end ? `${f.range_start} – ${f.range_end}` : "All dates"}
                </span>
                {f.notes && <div className="text-xs text-slate-400 mt-0.5">{f.notes}</div>}
              </div>
              <button
                type="button"
                onClick={() => handleRelease(f.freeze_id)}
                className="text-xs text-red-600 hover:underline ml-2 shrink-0"
              >
                Release
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function CalendarPreviewTab() {
  const [form, setForm] = useState({ employee_id: "", work_date: today() });
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const handlePreview = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    setResult(null);
    try {
      const r = await previewCalendarDay(form.employee_id, form.work_date);
      setResult(r);
    } catch (ex) {
      setErr(errMsg(ex));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-lg">
      <p className="text-sm text-slate-500 mb-4">
        Non-destructive calendar resolution preview — shows how the WF engine resolves an employee-day without saving anything.
      </p>
      {err && <div className="text-red-600 text-sm mb-3">{err}</div>}

      <form onSubmit={handlePreview} className="bg-white border rounded-xl p-4 space-y-3 mb-4">
        <EmployeeSelect
          label="Employee"
          required
          value={form.employee_id}
          onChange={(id) => setForm({ ...form, employee_id: id })}
          selectClassName="w-full border rounded-lg px-3 py-2 text-sm"
        />
        <DatePickerField
          label="Work date"
          value={form.work_date}
          onChange={(date) => setForm({ ...form, work_date: date })}
        />
        <button type="submit" disabled={loading} className="w-full bg-teal-600 text-white py-2 rounded-lg text-sm font-medium disabled:opacity-50">
          {loading ? "Resolving…" : "Preview calendar day"}
        </button>
      </form>

      {result && (
        <div className="bg-slate-50 border rounded-xl p-4 text-sm space-y-2">
          <div className="flex items-center gap-3">
            <StatusBadge status={result.resolved_status} />
            <span className="font-semibold text-slate-800">{result.resolved_status}</span>
            <span className="text-slate-500 text-xs">via {result.resolution_source}</span>
          </div>
          <div><span className="text-slate-500 text-xs">Payable fraction:</span> <span className="font-medium">{result.payable_fraction}</span></div>
          {result.shift_name && <div><span className="text-slate-500 text-xs">Shift:</span> {result.shift_name}</div>}
          {result.holiday_name && <div><span className="text-slate-500 text-xs">Holiday:</span> {result.holiday_name}</div>}
          {result.leave_type && <div><span className="text-slate-500 text-xs">Leave type:</span> {result.leave_type}</div>}
          {result.flags?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {result.flags.map((f) => (
                <span key={f} className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{f}</span>
              ))}
            </div>
          )}
          {result.conflicts?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {result.conflicts.map((c) => (
                <span key={c} className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">{c}</span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CorrectionRequestTab() {
  const [form, setForm] = useState({
    employee_id: "",
    work_date: today(),
    correction_type: "status",
    reason: "",
    requested_status: "",
    requested_fraction: "",
  });
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg("");
    setErr("");
    try {
      const requested_values = {};
      if (form.requested_status) requested_values.status = form.requested_status;
      if (form.requested_fraction !== "") requested_values.fraction = parseFloat(form.requested_fraction);
      await submitAttendanceCorrectionRequest({
        employee_id: form.employee_id,
        work_date: form.work_date,
        correction_type: form.correction_type,
        requested_values,
        reason: form.reason,
      });
      setMsg("Correction request submitted for review.");
      setForm({ ...form, employee_id: "", reason: "", requested_status: "", requested_fraction: "" });
    } catch (ex) {
      setErr(errMsg(ex));
    } finally {
      setSaving(false);
    }
  };

  const CORRECTION_TYPES = [
    { value: "status", label: "Status correction" },
    { value: "punch_time", label: "Punch time correction" },
    { value: "fraction", label: "Payable fraction correction" },
  ];

  return (
    <div className="max-w-lg">
      <p className="text-sm text-slate-500 mb-4">
        Submit an attendance correction request. Requests are routed through the approval workflow before any changes are applied.
      </p>
      {msg && <div className="text-green-700 text-sm mb-3">{msg}</div>}
      {err && <div className="text-red-600 text-sm mb-3">{err}</div>}

      <form onSubmit={handleSubmit} className="bg-white border rounded-xl p-4 space-y-3">
        <EmployeeSelect
          label="Employee"
          required
          value={form.employee_id}
          onChange={(id) => setForm({ ...form, employee_id: id })}
          selectClassName="w-full border rounded-lg px-3 py-2 text-sm"
        />
        <div className="flex gap-2">
          <DatePickerField
            label="Work date"
            className="flex-1"
            value={form.work_date}
            onChange={(date) => setForm({ ...form, work_date: date })}
          />
          <div className="flex-1">
            <label className="block text-xs font-medium text-slate-600 mb-1">Correction type</label>
            <select
              className="w-full border rounded-lg px-3 py-2 text-sm"
              value={form.correction_type}
              onChange={(e) => setForm({ ...form, correction_type: e.target.value })}
            >
              {CORRECTION_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
        </div>

        {form.correction_type === "status" && (
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Requested status</label>
            <select
              className="w-full border rounded-lg px-3 py-2 text-sm"
              value={form.requested_status}
              onChange={(e) => setForm({ ...form, requested_status: e.target.value })}
            >
              <option value="">— select —</option>
              {["P", "A", "HD", "L", "H", "WO"].map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
        )}
        {form.correction_type === "fraction" && (
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Requested fraction (0–1)</label>
            <input
              type="number"
              min="0"
              max="1"
              step="0.25"
              className="w-full border rounded-lg px-3 py-2 text-sm"
              value={form.requested_fraction}
              onChange={(e) => setForm({ ...form, requested_fraction: e.target.value })}
            />
          </div>
        )}

        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Reason</label>
          <textarea
            required
            rows={2}
            placeholder="Describe why this correction is needed"
            className="w-full border rounded-lg px-3 py-2 text-sm"
            value={form.reason}
            onChange={(e) => setForm({ ...form, reason: e.target.value })}
          />
        </div>
        <button type="submit" disabled={saving} className="w-full bg-slate-800 text-white py-2 rounded-lg text-sm font-medium disabled:opacity-50">
          {saving ? "Submitting…" : "Submit correction request"}
        </button>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main dashboard
// ---------------------------------------------------------------------------

export default function WfAttendanceDashboard() {
  const [activeTab, setActiveTab] = useState("daily");
  const [orgId, setOrgId] = useState("");

  useEffect(() => {
    fetchMyOrganisationSummary()
      .then((o) => setOrgId(o?.id || ""))
      .catch(() => {});
  }, []);

  return (
    <div className="p-6 w-full max-w-full min-w-0">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900">Workforce Attendance</h1>
        <p className="text-sm text-slate-500 mt-1">
          Enterprise attendance management — daily records, biometric ingest, freezes, overrides, calendar resolution.
        </p>
      </div>

      <div className="flex gap-1 mb-6 border-b overflow-x-auto">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
              activeTab === tab.id
                ? "border-teal-600 text-teal-700"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div>
        {activeTab === "daily" && <DailyAttendanceTab orgId={orgId} />}
        {activeTab === "biometric" && <BiometricIngestTab />}
        {activeTab === "freeze" && <FreezeManagerTab />}
        {activeTab === "calendar" && <CalendarPreviewTab />}
        {activeTab === "correction" && <CorrectionRequestTab />}
      </div>
    </div>
  );
}
